import { AxiosMode } from '@/axiosModel';

import { IMAGE_UPLOAD_ENDPOINT, uploadImageFile } from '@/api/upload';

export interface CharacterItem {
  id: string;
  name: string;
  age: number | undefined;
  gender: '男' | '女' | '不限' | '';
  personality: string[];
  catchphrase: string;
  artStyle: string;
  avatarUrl: string;
  generatedUrl: string;
  refImageUrl: string;
  description: string;
  createdAt: number;
  updatedAt: number;
  setting: string;
  status?: CharacterStatus;
  taskId?: string;
}

export type CharacterStatus = 'queued' | 'running' | 'processing' | 'completed' | 'failed';
export type CharacterCreate = Omit<CharacterItem, 'id' | 'createdAt' | 'updatedAt'>;

export interface CharacterListParams {
  page?: number;
  pagesize?: number;
  keyword?: string;
  status?: CharacterStatus;
  isDel?: number;
}

export interface CharacterListResult {
  list: CharacterItem[];
  total: number;
  page: number;
  pagesize: number;
}

interface BackendCharacterListResult {
  list?: BackendCharacterRecord[];
  total?: number;
  page?: number;
  pagesize?: number;
  pageSize?: number;
}

interface BackendCharacterRecord {
  characterId?: string;
  id?: string;
  taskId?: string | number;
  task_id?: string | number;
  latestTaskId?: string | number;
  latest_task_id?: string | number;
  name?: string;
  role?: string;
  description?: string;
  setting?: string;
  art_style?: string;
  status?: CharacterStatus | 'succeeded';
  thumbnail?: string;
  thumbnailUrl?: string;
  avatarUrl?: string;
  generatedUrl?: string;
  imageUrl?: string;
  image_url?: string;
  reference_image_url?: string;
  createTime?: number;
  updateTime?: number;
  isDel?: number;
  character?: BackendCharacterDetail;
  meta?: {
    createTime?: number;
    updateTime?: number;
    status?: CharacterStatus | 'succeeded';
    taskId?: string | number;
    task_id?: string | number;
    latestTaskId?: string | number;
    latest_task_id?: string | number;
  };
}

interface BackendCharacterDetail {
  id?: string;
  characterId?: string;
  taskId?: string | number;
  task_id?: string | number;
  latestTaskId?: string | number;
  latest_task_id?: string | number;
  name?: string;
  role?: string;
  appearance?: Record<string, unknown> | string | null;
  consistency_prompt?: string;
  setting?: string;
  personality?: string | string[];
  visual_keywords?: string | string[];
  thumbnail?: string;
  thumbnailUrl?: string;
  avatarUrl?: string;
  generatedUrl?: string;
  imageUrl?: string;
  image_url?: string;
  reference_image_url?: string;
  art_style?: string;
  description?: string;
}

interface CharacterCreateResponse {
  task_id?: string;
  taskId?: string;
  status?: CharacterStatus;
  characterId?: string;
  character_id?: string;
  id?: string;
}

export interface CharacterTaskResult {
  error?: unknown;
  image_url?: string;
  thumbnail?: string;
  thumbnailUrl?: string;
  avatarUrl?: string;
  generatedUrl?: string;
  imageUrl?: string;
  characterId?: string;
  character_id?: string;
  id?: string;
  name?: string;
  setting?: string;
  personality?: string | string[];
  description?: string;
  art_style?: string;
  reference_image_url?: string;
}

export interface CharacterTaskProgress {
  task_id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  progress?: number;
  message?: string;
  result?: CharacterTaskResult | null;
  error?: unknown;
  created_at?: number;
  metadata?: {
    id?: string;
    characterId?: string;
    name?: string;
    art_style?: string;
    reference_image_url?: string;
  };
}

const apis = new AxiosMode();
const API_BASE = AxiosMode.API_HOST;
const CHARACTER_API_BASE = `${API_BASE}/api/aipicturebook`;
export const CHARACTER_PAGE_SIZE = 100;
const CHARACTER_POLL_INTERVAL = 3000;

export const CHARACTER_REFERENCE_IMAGE_UPLOAD_ENDPOINT = IMAGE_UPLOAD_ENDPOINT;

import {
  CHARACTER_DEFAULT_ART_STYLE,
  CHARACTER_NO_STYLE_VALUE
} from '@/constants/characterArtStyles';

export async function fetchCharacterList(
  params: CharacterListParams = {}
): Promise<CharacterListResult> {
  const page = params.page ?? 1;
  const pagesize = params.pagesize ?? CHARACTER_PAGE_SIZE;
  const { req } = apis.post({
    postData: {
      ...params,
      page,
      pagesize,
      isDel: params.isDel ?? 1
    },
    remotePath: `${CHARACTER_API_BASE}/list-character`
  });
  const res = (await req) as { data: BackendCharacterListResult };
  const data = res.data ?? {};
  const list = data.list ?? [];

  return {
    list: list.map(mapBackendCharacter),
    total: Number(data.total ?? 0) || 0,
    page: Number(data.page ?? page) || page,
    pagesize: Number(data.pagesize ?? data.pageSize ?? pagesize) || pagesize
  };
}

export async function getCharacter(characterId: string): Promise<CharacterItem> {
  const { req } = apis.post({
    postData: { characterId },
    remotePath: `${CHARACTER_API_BASE}/get-character`
  });
  const res = (await req) as { data: BackendCharacterRecord };
  return mapBackendCharacter(res.data);
}

export async function createCharacter(
  data: CharacterCreate,
  characterId?: string
): Promise<CharacterItem> {
  const { req } = apis.post({
    postData: buildCreatePayload(data, characterId),
    remotePath: `${CHARACTER_API_BASE}/create-character`
  });
  let res: { data: CharacterCreateResponse };
  try {
    res = (await req) as { data: CharacterCreateResponse };
  } catch (error) {
    if (isInsufficientBalanceError(error)) {
      throw new Error('云币不足,请充值。');
    }
    throw error;
  }
  const taskId = toStringValue(res.data?.task_id || res.data?.taskId);
  const createdCharacterId = toStringValue(
    res.data?.characterId || res.data?.character_id || res.data?.id || characterId
  );

  if (!taskId) throw new Error('角色生成任务创建失败');
  if (!createdCharacterId) throw new Error('角色生成任务缺少角色ID');

  const now = Date.now();
  return {
    ...data,
    id: createdCharacterId,
    createdAt: now,
    updatedAt: now,
    status: res.data?.status ?? 'queued',
    taskId
  };
}

function isInsufficientBalanceError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const record = error as Record<string, unknown>;
  if (record.code === 'INSUFFICIENT BALANCE' || record.msg === 'INSUFFICIENT BALANCE') return true;

  const response = record.response as Record<string, unknown> | undefined;
  const responseData = response?.data as Record<string, unknown> | undefined;
  return (
    responseData?.code === 'INSUFFICIENT BALANCE' || responseData?.msg === 'INSUFFICIENT BALANCE'
  );
}

export async function updateCharacter(
  characterId: string,
  data: Partial<CharacterCreate>
): Promise<CharacterItem> {
  const { req } = apis.post({
    postData: {
      characterId,
      ...buildUpdatePayload(data)
    },
    remotePath: `${CHARACTER_API_BASE}/update-character`
  });
  const res = (await req) as { data?: BackendCharacterRecord };

  return mapBackendCharacter({
    characterId,
    ...(res.data ?? {})
  });
}

/** 上传角色参考图，通过图片审核接口返回可用地址 */
export async function uploadCharacterReferenceImage(file: File): Promise<string> {
  return uploadImageFile(file);
}

export async function deleteCharacter(characterId: string): Promise<void> {
  const { req } = apis.post({
    postData: { characterId },
    remotePath: `${CHARACTER_API_BASE}/delete-character`
  });
  await req;
}

export async function waitForCharacterTask(taskId: string): Promise<CharacterTaskProgress> {
  while (true) {
    const progress = await getCharacterTaskProgress(taskId);

    if (progress.status === 'succeeded') return progress;
    if (progress.status === 'failed') {
      if (
        typeof progress.result?.error === 'string' &&
        progress.result.error.includes('InputTextSensitiveContentDetected')
      ) {
        throw new Error('生成角色失败,图片内容敏感');
      }

      throw new Error(progress.message || '角色生成失败');
    }

    await delay(CHARACTER_POLL_INTERVAL);
  }
}

export async function getCharacterTaskProgress(taskId: string): Promise<CharacterTaskProgress> {
  const { req } = apis.post({
    postData: { taskId },
    remotePath: `${CHARACTER_API_BASE}/task-progress`
  });
  const res = (await req) as { data: CharacterTaskProgress };
  return res.data;
}

export function resolveCharacterTaskResult(
  progress: CharacterTaskProgress,
  source: CharacterItem
): CharacterItem {
  const result = progress.result ?? undefined;
  const generatedImageUrl = toStringValue(
    result?.imageUrl || result?.image_url || result?.generatedUrl || result?.reference_image_url
  );
  const thumbnailUrl = toStringValue(
    result?.thumbnail || result?.thumbnailUrl || result?.avatarUrl || generatedImageUrl
  );
  const artStyle = toStringValue(result?.art_style || progress.metadata?.art_style);

  return {
    ...source,
    id: toStringValue(
      source.id || result?.characterId || result?.character_id || result?.id || progress.task_id
    ),
    avatarUrl: thumbnailUrl || source.avatarUrl,
    generatedUrl: generatedImageUrl || source.generatedUrl,
    refImageUrl: toStringValue(result?.reference_image_url) || source.refImageUrl,
    setting: toStringValue(result?.setting) || source.setting,
    description: toStringValue(result?.description) || source.description,
    artStyle: artStyle ? toFormArtStyle(artStyle) : source.artStyle,
    updatedAt: Date.now(),
    status: 'completed',
    taskId: progress.task_id
  };
}

function buildCreatePayload(data: CharacterCreate, characterId?: string) {
  return removeEmptyFields({
    characterId,
    description: data.description?.trim(),
    setting: data.setting?.trim(),
    reference_image_url: data.refImageUrl || undefined,
    art_style: toApiArtStyle(data.artStyle)
  });
}

function buildUpdatePayload(data: Partial<CharacterCreate>) {
  return removeEmptyFields({
    name: data.name,
    description: data.description?.trim(),
    setting: data.setting?.trim()
  });
}

function mapBackendCharacter(data: BackendCharacterRecord): CharacterItem {
  const detail = data.character ?? {};
  const id = getCharacterId(data);
  const personality = normalizeStringArray(detail.personality);
  const visualKeywords = normalizeStringArray(detail.visual_keywords);

  const setting = toStringValue(detail.setting) || toStringValue(data.setting);
  const description = toStringValue(detail.description) || toStringValue(data.description);
  const artStyle = toStringValue(detail.art_style || data.art_style);
  const referenceImageUrl = toStringValue(detail.reference_image_url || data.reference_image_url);
  const generatedImageUrl = toStringValue(
    detail.imageUrl ||
      data.imageUrl ||
      detail.image_url ||
      data.image_url ||
      detail.generatedUrl ||
      data.generatedUrl ||
      referenceImageUrl
  );
  const thumbnail = toStringValue(
    detail.thumbnail ||
      data.thumbnail ||
      detail.thumbnailUrl ||
      data.thumbnailUrl ||
      detail.avatarUrl ||
      data.avatarUrl ||
      generatedImageUrl
  );
  const createTime = Number(data.createTime ?? data.meta?.createTime ?? 0) || 0;
  const updateTime = Number(data.updateTime ?? data.meta?.updateTime ?? createTime) || createTime;

  return {
    id,
    name: toStringValue(data.name || detail.name),
    age: undefined,
    gender: '',
    personality: personality.length ? personality : visualKeywords,
    catchphrase: '',
    artStyle: toFormArtStyle(artStyle),
    avatarUrl: thumbnail,
    generatedUrl: generatedImageUrl,
    refImageUrl: referenceImageUrl,
    description,
    setting,
    createdAt: createTime,
    updatedAt: updateTime,
    status: normalizeCharacterStatus(data.status || data.meta?.status),
    taskId: getCharacterTaskId(data)
  };
}

function getCharacterId(data: BackendCharacterRecord) {
  return toStringValue(
    data.characterId || data.id || data.character?.id || data.character?.characterId
  );
}

function getCharacterTaskId(data: BackendCharacterRecord) {
  return toStringValue(
    data.taskId ||
      data.task_id ||
      data.latestTaskId ||
      data.latest_task_id ||
      data.character?.taskId ||
      data.character?.task_id ||
      data.character?.latestTaskId ||
      data.character?.latest_task_id ||
      data.meta?.taskId ||
      data.meta?.task_id ||
      data.meta?.latestTaskId ||
      data.meta?.latest_task_id
  );
}

function normalizeCharacterStatus(status: unknown): CharacterStatus | undefined {
  const value = toStringValue(status);

  if (value === 'succeeded') return 'completed';
  if (
    value === 'queued' ||
    value === 'running' ||
    value === 'processing' ||
    value === 'completed' ||
    value === 'failed'
  ) {
    return value;
  }

  return undefined;
}

function normalizeStringArray(value: unknown) {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  if (typeof value !== 'string') return [];

  return value
    .split(/[、,，;；\s]+/u)
    .map((item) => item.trim())
    .filter(Boolean);
}

function toApiArtStyle(style: string) {
  if (!style || style === CHARACTER_NO_STYLE_VALUE) return '';
  return style;
}

function toFormArtStyle(style: string) {
  if (!style) return CHARACTER_DEFAULT_ART_STYLE;
  return style;
}

function toStringValue(value: unknown) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function removeEmptyFields(payload: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== '')
  );
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
